import { type Dispatch, type RefObject, type SetStateAction, useCallback, useEffect, useRef } from 'react'
import type { Swiper as SwiperInstance } from 'swiper'

export const useMediaGallerySwiper = ({
	activeIndex,
	canLoadMore,
	initialIndex,
	isDesktop,
	isGalleryZoomed,
	isMobileImageZoomed,
	isLoadingMore,
	itemsLength,
	onLoadMore,
	open,
	resetZoom,
	setActiveIndex,
	setIsChromeHidden,
	setIsReviewExpanded,
	total
}: {
	activeIndex: number
	canLoadMore: boolean
	initialIndex: number
	isDesktop: boolean
	isGalleryZoomed: boolean
	isMobileImageZoomed: boolean
	isLoadingMore: boolean
	itemsLength: number
	onLoadMore?: () => Promise<void> | void
	open: boolean
	resetZoom: () => void
	setActiveIndex: Dispatch<SetStateAction<number>>
	setIsChromeHidden: Dispatch<SetStateAction<boolean>>
	setIsReviewExpanded: Dispatch<SetStateAction<boolean>>
	total: number
}) => {
	const shouldLoop = isDesktop && itemsLength > 1
	const modalContentRef = useRef<HTMLDivElement>(null)
	const gallerySwiperRef = useRef<SwiperInstance | null>(null)
	const lastLoadMoreItemsLengthRef = useRef<number | null>(null)
	const shouldSlideToLastAfterLoadRef = useRef(false)
	const isLoadingAllMediaBeforeLastSlideRef = useRef(false)

	const slideToMediaIndex = useCallback(
		(nextIndex: number) => {
			const swiper = gallerySwiperRef.current
			if (swiper && !swiper.destroyed) {
				shouldLoop ? swiper.slideToLoop(nextIndex) : swiper.slideTo(nextIndex)
			}
			setActiveIndex(nextIndex)
		},
		[setActiveIndex, shouldLoop]
	)

	const slideToMediaIndexWithZoomReset = useCallback(
		(nextIndex: number) => {
			if (!isGalleryZoomed && !isMobileImageZoomed) {
				slideToMediaIndex(nextIndex)
				return
			}

			resetZoom()
			requestAnimationFrame(() => {
				slideToMediaIndex(nextIndex)
			})
		},
		[isGalleryZoomed, isMobileImageZoomed, resetZoom, slideToMediaIndex]
	)

	const handlePrevSlide = useCallback(() => {
		if (activeIndex === 0 && itemsLength < total && !isLoadingMore) {
			resetZoom()
			isLoadingAllMediaBeforeLastSlideRef.current = true
			shouldSlideToLastAfterLoadRef.current = true
			void onLoadMore?.()
			return
		}

		if (shouldLoop) {
			slideToMediaIndexWithZoomReset((activeIndex - 1 + itemsLength) % itemsLength)
			return
		}

		if (activeIndex > 0) slideToMediaIndexWithZoomReset(activeIndex - 1)
	}, [
		activeIndex,
		isLoadingMore,
		itemsLength,
		onLoadMore,
		resetZoom,
		shouldLoop,
		slideToMediaIndexWithZoomReset,
		total
	])

	const handleNextSlide = useCallback(() => {
		if (shouldLoop) {
			slideToMediaIndexWithZoomReset((activeIndex + 1) % itemsLength)
			return
		}

		if (activeIndex < itemsLength - 1) slideToMediaIndexWithZoomReset(activeIndex + 1)
	}, [activeIndex, itemsLength, shouldLoop, slideToMediaIndexWithZoomReset])

	const handleSlideChange = useCallback(
		(swiper: SwiperInstance) => {
			if (isGalleryZoomed || isMobileImageZoomed) return

			const nextIndex = shouldLoop ? swiper.realIndex : swiper.activeIndex

			if (nextIndex === activeIndex) return

			setActiveIndex(nextIndex)
			setIsReviewExpanded(false)
			resetZoom()
		},
		[activeIndex, isGalleryZoomed, isMobileImageZoomed, resetZoom, setActiveIndex, setIsReviewExpanded, shouldLoop]
	)

	const updateGallerySwiperAfterOpen = useCallback(
		(isOpen: boolean) => {
			if (!isOpen) return

			requestAnimationFrame(() => {
				const swiper = gallerySwiperRef.current
				if (!swiper || swiper.destroyed) return

				swiper.update()
				swiper.updateSlidesClasses()
				shouldLoop ? swiper.slideToLoop(initialIndex, 0, false) : swiper.slideTo(initialIndex, 0, false)
			})
		},
		[initialIndex, shouldLoop]
	)

	useEffect(() => {
		if (!open) return
		setActiveIndex(initialIndex)
		setIsReviewExpanded(false)
		setIsChromeHidden(false)
		resetZoom()
		const swiper = gallerySwiperRef.current
		if (!swiper || swiper.destroyed) return
		shouldLoop ? swiper.slideToLoop(initialIndex, 0) : swiper.slideTo(initialIndex, 0)
	}, [initialIndex, open, resetZoom, setActiveIndex, setIsChromeHidden, setIsReviewExpanded, shouldLoop])

	useEffect(() => {
		const swiper = gallerySwiperRef.current
		if (!open || !swiper || swiper.destroyed) return

		swiper.update()
		swiper.updateSlidesClasses()
	}, [itemsLength, open, shouldLoop])

	useEffect(() => {
		if (!shouldSlideToLastAfterLoadRef.current || isLoadingMore || !itemsLength) return
		if (itemsLength < total) return

		isLoadingAllMediaBeforeLastSlideRef.current = false
		shouldSlideToLastAfterLoadRef.current = false
		const lastIndex = itemsLength - 1
		const swiper = gallerySwiperRef.current
		if (swiper && !swiper.destroyed) {
			shouldLoop ? swiper.slideToLoop(lastIndex, 0) : swiper.slideTo(lastIndex, 0)
		}
		setActiveIndex(lastIndex)
	}, [isLoadingMore, itemsLength, setActiveIndex, shouldLoop, total])

	useEffect(() => {
		if (!open || !isLoadingAllMediaBeforeLastSlideRef.current || isLoadingMore) return
		if (itemsLength >= total) return

		void onLoadMore?.()
	}, [isLoadingMore, itemsLength, onLoadMore, open, total])

	useEffect(() => {
		if (open) return

		lastLoadMoreItemsLengthRef.current = null
		shouldSlideToLastAfterLoadRef.current = false
		isLoadingAllMediaBeforeLastSlideRef.current = false
		setIsChromeHidden(false)
		resetZoom()
	}, [open, resetZoom, setIsChromeHidden])

	useEffect(() => {
		if (!open || isLoadingMore || !canLoadMore) return
		if (activeIndex < itemsLength - 3) return
		if (isLoadingAllMediaBeforeLastSlideRef.current) return
		if (lastLoadMoreItemsLengthRef.current === itemsLength) return

		lastLoadMoreItemsLengthRef.current = itemsLength
		onLoadMore?.()
	}, [activeIndex, canLoadMore, isLoadingMore, itemsLength, onLoadMore, open])

	useEffect(() => {
		if (open) return

		modalContentRef.current?.querySelectorAll('video').forEach(video => {
			video.pause()
			video.currentTime = 0
		})
	}, [open])

	useEffect(() => {
		if (!open || itemsLength < 2) return

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.defaultPrevented) return

			const target = event.target as HTMLElement | null
			if (target?.closest('input, textarea, select, [contenteditable="true"]')) return

			if (event.key === 'ArrowLeft') {
				event.preventDefault()
				handlePrevSlide()
				return
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault()
				handleNextSlide()
			}
		}

		window.addEventListener('keydown', handleKeyDown)

		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [handleNextSlide, handlePrevSlide, itemsLength, open])

	return {
		gallerySwiperRef,
		handleNextSlide,
		handlePrevSlide,
		handleSlideChange,
		modalContentRef: modalContentRef as RefObject<HTMLDivElement>,
		shouldLoop,
		slideToMediaIndex,
		updateGallerySwiperAfterOpen
	}
}
